import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createContext, ReactNode, useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react'

import { effectivePermissions, hasPermission, type PermissionHolder, SSO_QUERY_PARAM } from '@iws/backoffice-api'

import { meta, rows } from '@/db/database'
import { OFFLINE_CREDIT_ALLOWANCE_KEY, RETURN_WINDOW_DAYS_KEY } from '@/db/repositories/orders'
import { AuthModel, BusinessModel } from '@/http/models'
import { refreshCatalog, restoreSetupFromTerminal } from '@/services/devices/setup'
import { RECEIPT_HEADER_KEY, ReceiptHeader } from '@/services/printer/receiptHeader'
import { Cashier, CASHIER_KEY, DEVICE_KEY, PROFILE_KEY } from '@/lib/device'
import { DeviceLink, getDeviceUid, readDeviceLink, writeDeviceLink } from '@/lib/deviceLink'
import { storage } from '@/lib/storage'
import { configService } from '@/services/ConfigService'
import { SentryService } from '@/services/SentryService'
import { syncEngine, SyncState } from '@/sync/SyncEngine'
import { PosModel, PosSync } from '@/types'

const TOKEN_KEY = 'access_token'
const SYSTEM_UID_KEY = 'system_uid'

/**
 * `unlinked`: this browser is not linked to the business yet, or was unlinked in the backoffice (POS.md O2); it shows a
 * code to approve. Local data stays through an unlink.
 * `signed_out` / `setting_up`: a new device, online password sign-in then setup (§15.10 step 2). `locked`: a set-up
 * device with no cashier, the PIN screen (step 3). `ready`: selling. `moving`: connected again for another branch; the
 * old branch's waiting records go up, then set-up starts again (POS.md §18.2).
 */
type Stage = 'demo' | 'unlinked' | 'signed_out' | 'setting_up' | 'locked' | 'ready' | 'moving'

interface SessionContextType {
  stage: Stage
  profile: PosModel.StaffProfile | null
  device: PosSync.Device | null
  /** The branch this device was connected for (POS.md §18.2); null on devices linked before branches were bound. */
  linkedBranchId: string | null
  cashier: Cashier | null
  /** Whether the acting cashier holds a permission; before a cashier, the password session's. */
  can: (permission: string) => boolean
  /**
   * Password sign-in: gives the sync token. On a set-up device it also makes this staff member the cashier, unless
   * `keepCashier` (signing in only so sync can continue, §15.10 Q30).
   */
  login: (email: string, password: string, options?: { keepCashier?: boolean }) => Promise<void>
  /** First setup of this device: it becomes that terminal of one of the staff member's branches, then pulls. */
  setUpDevice: (branchId: string, terminal: { _id: string; unique_no: number }) => Promise<void>
  /** PIN sign-in, checked against the staff records on the device; no network (§15.10 item 5). */
  unlock: (staffId: string, pin: string) => Promise<void>
  /** Back to the PIN screen; the sync session stays. */
  lock: () => void
  /**
   * Ends the sync session too. The device stays set up and keeps its data. Refused while records wait to sync: they
   * would wait for the next sign-in (POS.md O2, "logout needs a sync").
   */
  signOut: () => Promise<void>
  /** The browser was linked in the backoffice (pairing approved). */
  completeLink: (link: DeviceLink) => void
  /** Set after the backoffice unlinked this device: the link screen explains why. */
  unlinkedNotice: boolean
  /** Connected again for another branch and still sending the old branch's records (POS.md §18.2). */
  moving: boolean
}

const SessionContext = createContext<SessionContextType>(null!)

/** Business settings the till needs offline, refreshed after each sync and kept in the device database. */
const refreshSettings = async () => {
  const res = await BusinessModel.getProfile()
  if (!res.success || !res.data) return
  const pos = res.data.result.app_settings?.pos
  await meta.set(OFFLINE_CREDIT_ALLOWANCE_KEY, pos?.offline_credit_allowance ?? null)
  await meta.set(RETURN_WINDOW_DAYS_KEY, pos?.return_window_days ?? null)
  const { name, address, contact_number, email, registration_number, sst_id } = res.data.result
  const header: ReceiptHeader = {
    name: name ?? '',
    address,
    phone: contact_number,
    email,
    registration_no: registration_number,
    tax_id: sst_id,
    footer: pos?.receipt_footer || undefined,
    receipt: pos?.receipt ?? null,
  }
  await meta.set(RECEIPT_HEADER_KEY, header)
  // Device catalog (System Admin) and, on a new PC, the terminal's saved device setup (POS.md §18.9).
  await refreshCatalog().catch((error) => console.warn('[pos] device catalog', error))
  await restoreSetupFromTerminal().catch((error) => console.warn('[pos] device setup', error))
}

/** A staff member as the acting cashier, from the profile (password) or the synced staff and roles (PIN). */
/** Staff of the branch (`Branch.staff_ids`, pulled as the profile's `branches`), or an admin, who works everywhere. */
const worksIn = (profile: PosModel.StaffProfile, branchId: string) =>
  (profile.roles ?? []).some((role) => role.key === 'admin') || (profile.branches ?? []).some((b) => b._id === branchId)

const cashierOf = (staff: object, roles: object[]): Cashier => {
  const member = staff as { _id?: unknown; name?: unknown; permissions?: string[] }
  const { isAdmin, granted } = effectivePermissions({ permissions: member.permissions, roles: roles as PermissionHolder['roles'] })
  return { _id: String(member._id), name: String(member.name), permissions: granted, is_admin: isAdmin }
}

/**
 * Who is acting and which terminal this device is. Without `VITE_API_BASE_URL` the app runs on the demo API and
 * skips all of this. The sync token (the backend's 1-day JWT) and the cashier are separate: cashiers switch by PIN,
 * and an expired token only pauses sync until someone signs in with a password (§15.10 Q30).
 */
export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<PosModel.StaffProfile | null>(() => storage.read(PROFILE_KEY, null))
  const [device, setDevice] = useState<PosSync.Device | null>(() => storage.read(DEVICE_KEY, null))
  const [cashier, setCashierState] = useState<Cashier | null>(() => storage.read(CASHIER_KEY, null))
  const [token, setTokenState] = useState<string | null>(() => storage.read(TOKEN_KEY, null))
  const [link, setLink] = useState<DeviceLink | null>(() => readDeviceLink())
  const [unlinkedNotice, setUnlinkedNotice] = useState(false)
  const queryClient = useQueryClient()

  const moving = !!device && !!link?.branch_id && device.branch_id !== link.branch_id
  const stage: Stage = configService.isDemo()
    ? 'demo'
    : !link
      ? 'unlinked'
      : moving
        ? token
          ? 'moving'
          : 'signed_out'
      : !device
      ? token
        ? 'setting_up'
        : 'signed_out'
      : cashier
        ? 'ready'
        : 'locked'

  useEffect(() => {
    configService.setApiConfig({ terminalId: device?.terminal_id ?? null })
    SentryService.setContext({ host: configService.getConfig().apiConfig.clientHost, branch_id: device?.branch_id, terminal_id: device?.terminal_id, staff_id: cashier?._id })
  }, [device, cashier])

  const setToken = useCallback((next: string | null) => {
    storage.write(TOKEN_KEY, next)
    configService.setApiConfig({ bearerToken: next })
    setTokenState(next)
  }, [])

  const setCashier = useCallback(
    (next: Cashier | null) => {
      storage.write(CASHIER_KEY, next)
      setCashierState(next)
      // The till header shows the cashier from the register.
      queryClient.invalidateQueries({ queryKey: ['register'] })
    },
    [queryClient]
  )

  useEffect(() => {
    if (configService.isDemo()) return
    const systemUid = storage.read<string | null>(SYSTEM_UID_KEY, null)
    configService.setApiConfig({ systemUid, bearerToken: token, deviceUid: getDeviceUid() })
    if (!systemUid) {
      AuthModel.getSystemUid().then((res) => {
        if (!res.success || !res.data) return
        storage.write(SYSTEM_UID_KEY, res.data.system_uid)
        configService.setApiConfig({ systemUid: res.data.system_uid })
      })
    }
  }, [token])

  // Connected again for another branch (POS.md §18.2): the old branch's waiting records go up first (as its terminal,
  // which the server still accepts), then the device is set up again for the new branch. Retried until it works.
  useEffect(() => {
    if (configService.isDemo() || !moving || !token) return
    let done = false
    const attempt = async () => {
      if (done) return
      try {
        if (!(await syncEngine.leaveBranch())) return
      } catch (error) {
        console.error('[pos move branch]', error)
        return
      }
      done = true
      storage.write(DEVICE_KEY, null)
      setDevice(null)
      setCashier(null)
      toast.success('This device now works in its new branch', { description: 'Choose its terminal to finish.' })
    }
    attempt()
    const timer = setInterval(attempt, 30_000)
    window.addEventListener('online', attempt)
    return () => {
      done = true
      clearInterval(timer)
      window.removeEventListener('online', attempt)
    }
  }, [moving, token, setCashier])

  // Sync runs whenever the device is set up, even on the PIN screen, so data keeps flowing between cashiers.
  useEffect(() => {
    if (configService.isDemo() || !device || !link || moving) return
    syncEngine.onAuthExpired = () => setToken(null)
    // Unlinked in the backoffice: link and sign in again; the device keeps its data and pushes it once linked.
    syncEngine.onDeviceUnlinked = () => {
      writeDeviceLink(null)
      setLink(null)
      setUnlinkedNotice(true)
      storage.write(PROFILE_KEY, null)
      setProfile(null)
      setToken(null)
      setCashier(null)
    }
    syncEngine.start(device.branch_id)
    // Screens read the device database through React Query; a finished sync may have changed it.
    let last = syncEngine.getState().lastSyncedAt
    const unsubscribe = syncEngine.subscribe((state) => {
      if (state.lastSyncedAt !== last) {
        last = state.lastSyncedAt
        queryClient.invalidateQueries()
        refreshSettings()
      }
    })
    return () => {
      unsubscribe()
      syncEngine.stop()
      syncEngine.onAuthExpired = null
      syncEngine.onDeviceUnlinked = null
    }
  }, [device, link, moving, queryClient, setToken, setCashier])

  // A new token lets waiting records go.
  useEffect(() => {
    if (token && device) syncEngine.requestSync()
  }, [token, device])

  /** After a token is issued (password or SSO): load the profile and, on a set-up device, act as this staff member. */
  const adoptToken = useCallback(
    async (next: string, options?: { keepCashier?: boolean }) => {
      configService.setApiConfig({ bearerToken: next })
      const me = await AuthModel.getProfile()
      if (!me.success || !me.data) throw new Error(me.error?.message ?? 'Could not load your profile.')
      // A set-up till sells for one branch: only its staff (`Branch.staff_ids`) and admins may act on it.
      const setUp = storage.read<PosSync.Device | null>(DEVICE_KEY, null)
      // Signing in to move the device to its new branch isn't acting on the old one.
      const movingNow = !!setUp && !!readDeviceLink()?.branch_id && readDeviceLink()?.branch_id !== setUp.branch_id
      if (setUp && !options?.keepCashier && !movingNow && !worksIn(me.data.result, setUp.branch_id)) {
        configService.setApiConfig({ bearerToken: storage.read<string | null>(TOKEN_KEY, null) })
        throw new Error(`${me.data.result.name} does not work in this branch.`)
      }
      storage.write(PROFILE_KEY, me.data.result)
      setProfile(me.data.result)
      setToken(next)
      if (setUp && !options?.keepCashier && !movingNow) {
        setCashier(cashierOf(me.data.result, me.data.result.roles ?? []))
      }
    },
    [setToken, setCashier]
  )

  const login = useCallback(
    async (email: string, password: string, options?: { keepCashier?: boolean }) => {
      const res = await AuthModel.login({ email, password })
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Sign-in failed.')
      await adoptToken(res.data.authorization.token, options)
    },
    [adoptToken]
  )

  // SSO from admin-dashboard ("Open POS" → `?sso=<ticket>`): exchanged once, then removed from the address bar.
  // Only on a linked till; an unlinked one links first and the staff member signs in there.
  useEffect(() => {
    if (configService.isDemo() || !link) return
    const params = new URLSearchParams(window.location.search)
    const ticket = params.get(SSO_QUERY_PARAM)
    if (!ticket) return
    params.delete(SSO_QUERY_PARAM)
    window.history.replaceState(null, '', `${window.location.pathname}${params.size ? `?${params}` : ''}`)
    AuthModel.ssoExchange(ticket).then((res) => {
      if (res.success && res.data) adoptToken(res.data.authorization.token).catch((e) => console.error('[pos sso]', e))
    })
  }, [link, adoptToken])

  const setUpDevice = useCallback(
    async (branchId: string, terminal: { _id: string; unique_no: number }) => {
      if (!profile) throw new Error('Sign in first.')
      await syncEngine.initialise(branchId)
      const next: PosSync.Device = {
        staff_id: profile._id,
        terminal_id: terminal._id,
        terminal_unique_no: terminal.unique_no,
        branch_id: branchId,
      }
      storage.write(DEVICE_KEY, next)
      setCashier(cashierOf(profile, profile.roles ?? []))
      setDevice(next)
    },
    [profile, setCashier]
  )

  const unlock = useCallback(
    async (staffId: string, pin: string) => {
      const staff = await rows('users').where('_id').equals(staffId).first()
      // A plain comparison: the PIN says who is acting, it is not a security boundary (§15.10 item 5).
      if (!staff || !staff.offline_pin || String(staff.offline_pin) !== pin) throw new Error('Wrong PIN.')
      const roleIds = ((staff.role_ids as string[] | undefined) ?? []).map(String)
      const roles = (await rows('roles').toArray()).filter((role) => roleIds.includes(String(role._id)))
      setCashier(cashierOf(staff, roles))
    },
    [setCashier]
  )

  const lock = useCallback(() => setCashier(null), [setCashier])

  const completeLink = useCallback((next: DeviceLink) => {
    writeDeviceLink(next)
    setLink(next)
    setUnlinkedNotice(false)
  }, [])

  const signOut = useCallback(async () => {
    const pending = await syncEngine.countPending()
    if (pending > 0) {
      syncEngine.requestSync()
      throw new Error(`${pending} record(s) have not synced yet. Connect and let them sync before signing out.`)
    }
    storage.write(PROFILE_KEY, null)
    setProfile(null)
    setToken(null)
    setCashier(null)
  }, [setToken, setCashier])

  const acting = cashier ?? (profile ? cashierOf(profile, profile.roles ?? []) : null)
  const can = useCallback(
    (permission: string) =>
      stage === 'demo' ||
      (!!acting && hasPermission({ isAdmin: acting.is_admin, granted: acting.permissions }, permission)),
    [stage, acting]
  )

  return (
    <SessionContext.Provider
      value={{ stage, profile, device, linkedBranchId: link?.branch_id ?? null, cashier, can, login, setUpDevice, unlock, lock, signOut, completeLink, unlinkedNotice, moving }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => useContext(SessionContext)

export const useSyncState = (): SyncState => useSyncExternalStore(syncEngine.subscribe, syncEngine.getState)
